// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import Link, { StyledClickable, StyledIcon, StyledTypography } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getIsMoblico } from "@mcmc/App/Store/Selectors/LayoutSelectors";
import { screen } from "@mcmc/XDL/Helpers";
import { layoutContainer, Roboto } from "@mcmc/XDL/Utilities/sharedStyles";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface FooterContainerStyles {
    widgetWrapper?: InjectableCss;
    topContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    topSectionGridItem?: GridItemProps;
    topSectionGridContainer?: GridContainerProps;
    column1GridItem?: GridItemProps;
    column1LinkListWrapper?: InjectableCss;
    column1LinkWrapper?: InjectableCss;
    column2GridItem?: GridItemProps;
    column3GridItem?: GridItemProps;
    column4GridItem?: GridItemProps;
    bottomSectionGridItem?: GridItemProps;
    siteMapLinksWrapper?: InjectableCss;
    copyrightMessageGridItemAndLogoWrapper?: InjectableCss;
    copyrightMessageWrapperStyles?: InjectableCss;
    logoWrapper?: InjectableCss;
    linkList2Wrapper?: InjectableCss;
    linkList3Wrapper?: InjectableCss;
    linkList4Wrapper?: InjectableCss;
    socialLinksWrapper?: InjectableCss;
    partnerLogos?: InjectableCss;
}

const linkListStyles = css`
    margin-bottom: 18px;
    ${StyledWrapper} {
        padding: 0;
        margin: 0;
        line-height: inherit;
        ${StyledWrapper}:first-child {
            .title-wrapper {
                ${StyledTypography}, ${TypographyStyle} {
                    font-size: 14px;
                    font-weight: bold;
                    text-transform: uppercase;
                    color: ${getColor("text.inverse")};
                    padding-bottom: 7px;
                    font-family: ${Roboto.fontFamily};
                    letter-spacing: 0.16px;
                    line-height: 16px;
                    text-transform: uppercase;
                }
            }
        }
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

export const footerContainerStyles: FooterContainerStyles = {
    widgetWrapper: {
        css: css`
            background-color: ${getColor("footer.main")};
            display: flex;
            justify-content: center;
            ${StyledClickable} {
                ${StyledTypography} {
                    color: ${getColor("common.ui2")};
                    font-size: 14px;
                    letter-spacing: 0.16px;
                    line-height: 16px;
                    text-transform: capitalize;
                    font-weight: normal;
                }
                &:hover,
                &:focus {
                    ${StyledTypography} {
                        color: ${getColor("text.inverse")};
                        text-decoration: underline;
                    }
                }

                &:focus ${LazyImageStyle} {
                    outline-offset: 1px;
                    outline-color: ${getColor("text.inverse")};
                    outline-style: ${getProp("theme.focus.style", "solid")};
                    outline-width: ${getProp("theme.focus.width", "2px")};
                }
            }

            &.moblico-footer {
                .top-container {
                    padding-top: 15px;
                }

                .copyright-Logo-wrapper {
                    margin-top: 0;
                }

                .copyright {
                    margin: 0;
                    ${screen("lte", "sm")`
                        padding-bottom: 15px;
                    `}
                }
            }
        `,
    },
    topContainer: {
        css: css`
            ${layoutContainer}
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 45px 0 15px 0;
        `,
    },
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    topSectionGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    topSectionGridContainer: {},
    column1GridItem: {
        width: [12, 6, 3, 3, 3],
    },
    column1LinkListWrapper: {
        css: css`
            ${StyledClickable} {
                ${StyledTypography} {
                    font-weight: 500;
                    text-transform: uppercase;
                    margin-bottom: 18px;
                }
            }
        `,
    },
    column1LinkWrapper: {
        css: css`
            padding: 1px 0 0 0;
        `,
    },
    column2GridItem: {
        width: [12, 6, 3, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;

            ${StyledClickable} {
                ${StyledTypography} {
                    line-height: 20px;
                }
            }
        `,
    },
    column3GridItem: {
        width: [12, 6, 3, 3, 3],
    },
    column4GridItem: {
        width: [12, 6, 3, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-end;
            ${screen("lte", "sm")`
                align-items: flex-start;
            `}
        `,
    },
    bottomSectionGridItem: {
        width: 12,
        css: css`
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
            ${screen("lte", "sm")`
               flex-direction: column;
               order: 1;
            `}
        `,
    },
    siteMapLinksWrapper: {
        css: css`
            display: flex;
            align-items: center;
            ${screen("lte", "sm")`
                width: 100%;
                padding: 15px;
            `}
            ${StyledWrapper} {
                margin: 0;
                line-height: inherit;
                ${screen("lte", "sm")`
                   text-align: center;
                `}
                ${StyledWrapper} {
                    padding: 0;
                    margin: 0;
                }
            }
            ${StyledClickable} {
                ${StyledTypography} {
                    font-size: 12px;
                    line-height: 14px;
                }
                margin-right: 10px;
                padding-bottom: 33px;
                ${screen("lte", "xs")`
                    padding-bottom: 0;
                `}
                &::after {
                    margin-left: 10px;
                    content: "|";
                    color: ${getColor("common.ui2")};
                }
            }
            .list-wrapper {
                .links-wrapper:last-child {
                    ${StyledClickable} {
                        margin-right: 0;
                        &::after {
                            margin-left: 0;
                            content: "";
                        }
                    }
                }
            }
        `,
    },
    copyrightMessageGridItemAndLogoWrapper: {
        css: css`
            margin-bottom: 14px;
            ${screen("lte", "sm")`
                margin-bottom: 0;
            `}
            & > div {
                width: auto;
            }
            display: flex;
            align-items: center;
            flex-grow: 1;
            ${screen("lte", "sm")`
                order: 1;
            `}
            ${screen("lte", "xs")`
                margin-top: 20px;
                flex-wrap: wrap;
                & >:first-child {
                    margin-right: 0;
                    margin-bottom: 10px;
                }
            `}
        `,
    },
    copyrightMessageWrapperStyles: {
        css: css`
            margin-left: 25px;
            margin-right: 10px;
            color: ${getColor("common.ui2")};
            font-size: 12px;
            line-height: 15px;
            letter-spacing: 0.14px;
            ${screen("lte", "xs")`
                margin-left: 0;
                margin-right: 0;
            `}
        `,
    },
    linkList2Wrapper: {
        css: linkListStyles,
    },
    linkList3Wrapper: {
        css: linkListStyles,
    },
    linkList4Wrapper: {
        css: linkListStyles,
    },
    socialLinksWrapper: {
        css: css`
            ${StyledIcon} {
                margin: 5px 10px 0 0;
            }
            .link-wrapper {
                width: 41px;
            }
        `,
    },
    partnerLogos: {
        css: css``,
    },
};

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}
const mapStateToProps = (state: McMcApplicationState) => {
    const headerLinkListLinkFields = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    });

    return {
        headerLinks: (headerLinkListLinkFields?.fields?.links || []) as LinkModel[],
        categoryListPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
        isMoblico: getIsMoblico(state),
    };
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;
const FooterContainer: FC<Props> = ({ id, extendedStyles, headerLinks, categoryListPageUrl, isMoblico }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const headerMainNavigationStateLinks = useGetLinks(headerLinks, o => {
        if (o.fields.destination.type === "Category" && o.fields.destination.value === emptyGuid) {
            return {
                type: "Url",
                value: categoryListPageUrl || "",
            };
        }
        return o.fields.destination;
    });

    return (
        <StyledWrapper {...styles.widgetWrapper} className={isMoblico ? "moblico-footer" : ""}>
            <GridContainer {...styles.topContainer} className="top-container">
                {!isMoblico && (
                    <GridItem {...styles.topSectionGridItem}>
                        <GridContainer {...styles.topSectionGridContainer}>
                            <GridItem {...styles.column1GridItem}>
                                <StyledWrapper {...styles.column1LinkListWrapper}>
                                    {headerMainNavigationStateLinks.map((link, index) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper key={index} {...styles.column1LinkWrapper}>
                                            {link.url && (
                                                <Link
                                                    href={link.url}
                                                    target={headerLinks[index].fields.openInNewWindow ? "_blank" : ""}
                                                    {...((
                                                        (headerLinks[index].fields as any).overrideTitle || link.title
                                                    )?.toLowerCase() === "all products" && { id: "allproductsfooter" })}
                                                >
                                                    {headerLinks[index].fields.destination.value === emptyGuid
                                                        ? (headerLinks[index].fields as any).overrideTitle ||
                                                          translate("Products")
                                                        : (headerLinks[index].fields as any).overrideTitle ||
                                                          link.title}
                                                </Link>
                                            )}
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.column2GridItem}>
                                <StyledWrapper {...styles.linkList2Wrapper}>
                                    <Zone zoneName="LinkList2" contentId={id} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.linkList3Wrapper}>
                                    <Zone zoneName="LinkList3" contentId={id} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.linkList4Wrapper}>
                                    <Zone zoneName="LinkList4" contentId={id} />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.column3GridItem}>
                                <Zone zoneName="ContactUs" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.column4GridItem}>
                                <StyledWrapper {...styles.socialLinksWrapper}>
                                    <Zone zoneName="SocialLinks" contentId={id} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.partnerLogos}>
                                    <Zone zoneName="PartnerLogos" contentId={id} />
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}
                <GridItem {...styles.bottomSectionGridItem}>
                    <StyledWrapper
                        {...styles.copyrightMessageGridItemAndLogoWrapper}
                        className="copyright-Logo-wrapper"
                    >
                        {!isMoblico && <Zone zoneName="Logo" contentId={id} />}
                        <StyledWrapper {...styles.copyrightMessageWrapperStyles} className="copyright">
                            {siteMessage("McMc_Footer_Copyright", String(new Date().getFullYear()))}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.siteMapLinksWrapper}>
                        <Zone zoneName="SiteMapLinks" contentId={id} />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: connect(mapStateToProps, null)(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
